import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
  FunctionComponent
} from 'react';
import { BannerVariant } from '@/components/GlobalBanner';

export interface BannerAction {
  label: string;
  onClick: () => void;
}

export interface BannerState {
  isVisible: boolean;
  variant: BannerVariant;
  message: string;
  action?: BannerAction;
  dismissible?: boolean;
}

export interface GlobalBannerContextType {
  bannerState: BannerState;
  showWarning: (message: string, action?: BannerAction, dismissible?: boolean) => void;
  showError: (message: string, action?: BannerAction, dismissible?: boolean) => void;
  showInfo: (message: string, action?: BannerAction, dismissible?: boolean) => void;
  showSuccess: (message: string, action?: BannerAction, dismissible?: boolean) => void;
  hideBanner: () => void;
}

const GlobalBannerContext = createContext<GlobalBannerContextType | undefined>(undefined);

interface GlobalBannerProviderProps {
  children: ReactNode;
}

const initialBannerState: BannerState = {
  isVisible: false,
  variant: 'warning',
  message: '',
  action: undefined,
  dismissible: false
};

export const GlobalBannerProvider: FunctionComponent<GlobalBannerProviderProps> = ({ children }) => {
  const [bannerState, setBannerState] = useState<BannerState>(initialBannerState);

  const showWarning = useCallback((message: string, action?: BannerAction, dismissible = false) => {
    setBannerState({
      isVisible: true,
      variant: 'warning',
      message,
      action,
      dismissible
    });
  }, []);

  const showError = useCallback((message: string, action?: BannerAction, dismissible = false) => {
    setBannerState({
      isVisible: true,
      variant: 'error',
      message,
      action,
      dismissible
    });
  }, []);

  const showInfo = useCallback((message: string, action?: BannerAction, dismissible = true) => {
    setBannerState({
      isVisible: true,
      variant: 'info',
      message,
      action,
      dismissible
    });
  }, []);

  const showSuccess = useCallback((message: string, action?: BannerAction, dismissible = true) => {
    setBannerState({
      isVisible: true,
      variant: 'success',
      message,
      action,
      dismissible
    });
  }, []);

  const hideBanner = useCallback(() => {
    setBannerState((prev) => ({
      ...prev,
      isVisible: false
    }));
  }, []);

  const value: GlobalBannerContextType = useMemo(
    () => ({
      bannerState,
      showWarning,
      showError,
      showInfo,
      showSuccess,
      hideBanner
    }),
    [bannerState, showWarning, showError, showInfo, showSuccess, hideBanner]
  );

  return <GlobalBannerContext value={value}>{children}</GlobalBannerContext>;
};

export const useGlobalBanner = (): GlobalBannerContextType => {
  const context = useContext(GlobalBannerContext);
  if (context === undefined) {
    throw new Error('useGlobalBanner must be used within a GlobalBannerProvider');
  }
  return context;
};
